<template>
  <FusionBaseDialog
    :open="open"
    @close="close"
    @after-leave="afterLeave"
    :persistentOverlay="true"
    padded
  >
    <template v-slot:header>
      <v-card-item prepend-icon="mdi-information-outline">
        <v-card-title data-cy="deviceConnectedAfterStartIncompleteDataDialogHeader">
          {{ $t('deviceConnectedAfterStartIncompleteDataDialog.title') }}
        </v-card-title>
      </v-card-item>
    </template>
    <div data-cy="deviceConnectedAfterStartIncompleteDataDialogContent">
      {{ $t('deviceConnectedAfterStartIncompleteDataDialog.intro') }}
    </div>
    <div v-if="connectedAfterStartNotifications.length" data-cy="connectedAfterStartDeviceText">
      <p>
        {{ $t('deviceConnectedAfterStartIncompleteDataDialog.connectedAfterStart') }}
      </p>
      <ul
        v-if="connectedAfterStartNotifications.length"
        class="deviceList"
        data-cy="connectedAfterStartDeviceList"
      >
        <li
          v-for="(notification, index) in connectedAfterStartNotifications"
          :key="index"
          class="deviceListItem"
        >
          {{ deviceLabel(notification) }}
        </li>
      </ul>
    </div>
    <div v-if="incompleteDataNotifications.length" data-cy="incompleteDataDeviceText">
      <p>
        {{ $t('deviceConnectedAfterStartIncompleteDataDialog.incompleteData') }}
      </p>
      <ul
        v-if="incompleteDataNotifications.length"
        class="deviceList"
        data-cy="incompleteDataDeviceList"
      >
        <li
          v-for="(notification, index) in incompleteDataNotifications"
          :key="index"
          class="deviceListItem"
        >
          {{ deviceLabel(notification) }}
        </li>
      </ul>
    </div>
    <template v-slot:actions>
      <v-spacer></v-spacer>
      <v-btn
        @click="close"
        class="bg-primary text-white"
        variant="text"
        data-cy="deviceConnectedAfterStartIncompleteDataDialogDone"
      >
        {{ $t('buttons.done') }}
      </v-btn>
    </template>
  </FusionBaseDialog>
</template>
<script lang="ts">
import { defineComponent } from 'vue'
import { ErrorState } from '@/services/device-error-states'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS } from '@/store/devices'

export interface DeviceNotification {
  deviceId: string
  deviceName?: string
  deviceSerial?: string
  error: ErrorState
  studyId?: string
  shown?: boolean
  isActive?: boolean
}

export default defineComponent({
  name: 'DeviceConnectedAfterStartIncompleteDataDialog',
  data: () => ({
    open: false,
    notifications: [] as DeviceNotification[]
  }),
  watch: {
    errors() {
      this.updateNotifications()
      this.checkIfShouldShow()
    },
    telemetryStatuses() {
      this.updateNotifications()
      this.checkIfShouldShow()
    }
  },
  computed: {
    deviceIds(): string[] {
      return Object.keys(this.$store.state.devices.allDevices)
    },
    telemetryStatuses(): Record<string, boolean> {
      const statuses: Record<string, boolean> = {}
      this.deviceIds.forEach((id: string) => {
        statuses[id] =
          this.$store.getters[
            `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_TELEMETRY_24HRS_ACTIVE_BY_DEVICE_ID}`
          ](id)
      })
      return statuses
    },
    errors(): DeviceNotification[] {
      const connectedAfterStart: string[] =
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICES_WITH_CONNECTED_AFTER_START_ERROR}`
        ]
      const incompleteData: string[] =
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICES_WITH_INCOMPLETE_DATA_ERROR}`
        ]
      const devices: DeviceNotification[] = [
        ...connectedAfterStart.map((deviceId: string) => ({
          deviceId,
          error: ErrorState.CONNECTED_AFTER_START,
          isActive: this.telemetryStatuses[deviceId]
        })),
        ...incompleteData.map((deviceId: string) => ({
          deviceId,
          error: ErrorState.INCOMPLETE_DATA,
          isActive: this.telemetryStatuses[deviceId]
        }))
      ]

      return devices.reduce<DeviceNotification[]>((acc, { deviceId, error, isActive }) => {
        if (isActive) {
          const { deviceName, deviceSerial } = this.getDeviceDataFromId(deviceId)
          acc.push({
            deviceId,
            deviceName,
            deviceSerial,
            error
          })
        }
        return acc
      }, [])
    },
    connectedAfterStartNotifications(): DeviceNotification[] {
      return this.notifications.filter(
        (notification: DeviceNotification) =>
          notification.error === ErrorState.CONNECTED_AFTER_START && !notification.shown
      )
    },
    incompleteDataNotifications(): DeviceNotification[] {
      return this.notifications.filter(
        (notification: DeviceNotification) =>
          notification.error === ErrorState.INCOMPLETE_DATA && !notification.shown
      )
    },
    getIsTelemetry24HrsActiveByDeviceId() {
      return (deviceId: string) =>
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_TELEMETRY_24HRS_ACTIVE_BY_DEVICE_ID}`
        ](deviceId)
    }
  },
  methods: {
    deviceLabel(notification: DeviceNotification | null) {
      if (!notification) return ''

      return `${notification.deviceName ?? ''} (SN: ${notification.deviceSerial ?? ''})`
    },
    close() {
      this.open = false
    },
    afterLeave() {
      this.notifications.forEach((notification: DeviceNotification) => {
        notification.shown = true
      })
    },
    getNotification(errorObj: DeviceNotification) {
      const { deviceId, error } = errorObj
      if (!deviceId || !error) {
        return null
      }
      const studyId =
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_TELEMETRY_STUDY_ID_BY_DEVICE_ID}`
        ](deviceId)
      if (!studyId) {
        return null
      }

      const sameStudyError = this.notifications.find(
        (notification: DeviceNotification) =>
          notification.studyId === studyId && notification.error === error
      )
      const notification = {
        ...errorObj,
        studyId,
        shown: false
      }

      if (sameStudyError && sameStudyError.shown && sameStudyError.error === notification.error) {
        return null
      }

      return notification
    },
    updateNotifications() {
      let notification: DeviceNotification | null
      const newNotificationsToAdd: DeviceNotification[] = []
      this.errors.forEach((error: DeviceNotification) => {
        notification = this.getNotification(error)
        if (notification) {
          newNotificationsToAdd.push(notification)
        }
      })

      // Replace existing not-shown notifications with the new ones. This keeps the list up-to-date
      // by removing any notification that is too old to show. Keeping the shown ones allows to not
      // show them again in case any other error happens.
      const shownNotifications = this.notifications.filter(
        (notification: DeviceNotification) => notification.shown
      )
      this.notifications = [...shownNotifications, ...newNotificationsToAdd]
    },
    checkIfShouldShow() {
      if (this.incompleteDataNotifications.length || this.connectedAfterStartNotifications.length) {
        this.open = true
      } else {
        this.open = false
      }
    },
    getDeviceDataFromId(deviceId: string) {
      const deviceName: string =
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
          deviceId
        )
      const deviceSerial: string =
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
          deviceId
        )
      return {
        deviceName,
        deviceSerial
      }
    }
  }
})
</script>
<style lang="scss" scoped>
.deviceList {
  margin-bottom: 15px;

  .deviceListItem {
    list-style: none;
  }
}
</style>
